import type { ChangeEvent, ElementType, MouseEvent, RefObject } from 'react';
import { useState } from 'react';
import { sprintf } from 'sprintf-js';

import type { ApiFileUploadTypes } from 'api/types/ApiFileUploadTypes';
import { Box } from 'components/Box/Box';
import { Button } from 'components/Button/Button';
import type { FieldCustomErrorMatcher } from 'components/Form/Field/FieldCustomErrorMatcher';
import { InputField } from 'components/Form/InputField';
import { removeArrayIndex } from 'utils/functional/array';
import { uuid } from 'utils/functional/string/uuid';

export type MultiInputExtraData = {
  customErrorMatch?: FieldCustomErrorMatcher;
  includeCity?: boolean;
  includeFull?: boolean;
  label?: string;
  disabled?: boolean;
  placeholder?: string;
  isRequired?: boolean;
  fileType?: ApiFileUploadTypes;
  mimetypes?: string[];
  type?: 'FILE' | 'URL' | 'TEXT' | 'OPTION_SINGLE' | 'OPTION_MANY';
  options?: Record<'value', string>[];
  downloadAttachment?: boolean;
  // TODO: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onUpdateValue?: (...args: any[]) => any;
  listingType?: string;
  qaId?: string;
};

type Props = {
  id?: string;
  component?: ElementType | null | undefined;
  name: string;
  values: (string | Record<string, unknown>)[];
  errorRef?: RefObject<HTMLDivElement | null>;
  maxInputs: number;
  onChange: (event: (oldValues: unknown[]) => unknown[]) => void;
  type?: string;
  placeholder?: string;
  disabled?: boolean;
  extraData?: MultiInputExtraData[];
  hideControls?: boolean;
  addText?: string;
  removeText?: string;
  showEmpty?: boolean;
  qaId: string;
};

export function MultiInput({
  id,
  component,
  name,
  values,
  errorRef,
  maxInputs,
  onChange,
  type = 'text',
  placeholder,
  disabled = false,
  extraData = [],
  hideControls = false,
  addText,
  showEmpty,
  qaId,
}: Props) {
  const inputs = values.slice(0, maxInputs).map((value, index) => ({
    value,
    index,
  }));

  const Component = component ?? InputField;
  const [ids, setIds] = useState(
    Array.from({ length: values.length || 1 }).map(() => uuid()),
  );

  const onInputChange =
    (index: number) => (ev: ChangeEvent<HTMLInputElement>) => {
      let update;

      const valuesToAdd = ev && ev.target ? ev.target.value : ev;

      if (showEmpty && values.length > 1 && !ev) {
        update = (oldValues: unknown[]) => removeArrayIndex(oldValues, index);

        setIds(removeArrayIndex(ids, index));
      } else {
        update = (oldValues: unknown[]) => {
          const newValues = [...oldValues];
          newValues[index] = valuesToAdd;

          if (showEmpty && newValues.length >= values.length) {
            newValues.push('');
          }

          if (newValues.length > values.length) {
            setIds([...ids, uuid()]);
          }

          return newValues;
        };
      }

      onChange(update);
    };

  const addNewInput = () => {
    debugger;
    const newValues = [...values, ''];
    onChange(() => newValues);
    setIds([...ids, uuid()]);
  };

  const removeInputAtIndex = (index: number) => {
    onChange(() => removeArrayIndex(values, index));
    setIds(removeArrayIndex(ids, index));
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const inputId = (index: any) => {
    if (!id) return undefined;
    return index === 0 ? id : `${id}-${index + 1}`;
  };

  return (
    <>
      {inputs.map(({ index }) => (
        <Box
          key={ids[index] || index}
          style={{
            alignItems: 'center',
            display: 'flex',
            marginBottom: component ? 0 : '8px',
          }}
        >
          <Component
            id={inputId(index)}
            name={`${name}.${index}`}
            type={type}
            placeholder={placeholder}
            onChange={onInputChange(index)}
            disabled={disabled}
            data-qa-id={qaId}
            noMargin={component ? undefined : true}
            // eslint-disable-next-line react/jsx-props-no-spreading
            {...extraData[index]}
          />
          <div ref={errorRef} />
          {!hideControls && values.length > 1 && (
            <Button
              type="button"
              data-qa-id={`${qaId}-remove-${index}`}
              onClick={(e: MouseEvent<HTMLButtonElement>) => {
                e.preventDefault();

                if (!disabled) {
                  removeInputAtIndex(index);
                }
              }}
              variant="tertiary-grey"
              icon={{ name: 'trash', type: 'normalized' }}
            />
          )}
        </Box>
      ))}

      {!hideControls && values.length < maxInputs && (
        <Box mt="16px">
          <Button
            type="button"
            onClick={(e) => {
              e.preventDefault();

              if (!disabled) {
                addNewInput();
              }
            }}
            data-qa-id={`${qaId}-add`}
            variant="secondary"
            icon={{ name: 'plus', type: 'normalized' }}
          >
            {addText
              ? sprintf(getText('Add %(text)s'), { text: addText })
              : null}
          </Button>
        </Box>
      )}
    </>
  );
}
